import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import invariant from "invariant";
import { forwardRef, useCallback, useImperativeHandle, useState, type FC, type ForwardedRef, type RefAttributes } from "react";

export type DialogRefType<T = void> = {
	openDialog: (data: T) => void;
	closeDialog: () => void;
}
export type DialogResponseType<R> = { isCancelled: true } | { isCancelled: false, value: R };
export type DialogProps<T = void, R = void> = {
	isOpen: boolean,
	close: (response: DialogResponseType<R>) => void,
	data: T,
}
declare type Parameter<T extends (...args: any) => any> = T extends (arg: infer P, ...args: any) => any ? P : never
export type ExtractRefDialogType<T extends (...args: any) => any> = Parameter<T> extends RefAttributes<DialogRefType<infer R>> ? DialogRefType<R> : never;

export function withDialogHoC<T, R = unknown>(Component: FC<DialogProps<T, R>>, options?: { closeOnEscape?: boolean, closeOnClick?: boolean }) {
	return forwardRef(function DialogComponent({ width, callback }: { width: number, callback?: (response: DialogResponseType<R>) => void }, ref: ForwardedRef<DialogRefType<T>>) {
		const [row, setRow] = useState<T>();
		const [isOpen, setIsOpen] = useState<boolean>(false);
		const onMouseDown = useCallback(() => {
			if (!options?.closeOnClick) return;
			setIsOpen(false);
		}, []);
		const onKeyDown = (key: string) => {
			if (!options?.closeOnEscape) return;
			if (key !== "Escape") return;
			setIsOpen(false)
		}
		const closeMe = useCallback((response: DialogResponseType<R>) => {
			setIsOpen(false);
			callback?.(response);
		}, [callback]);
		useImperativeHandle(ref, () => ({
			openDialog: (rowData: T) => {
				setRow(rowData);
				setIsOpen(true);
			},
			closeDialog: () => {
				setIsOpen(false);
			},
		}));
		return <Dialog open={isOpen} as="div" className="relative z-10 focus:outline-none" onClose={onMouseDown} onKeyDown={e => onKeyDown(e.key)}>
			<DialogBackdrop transition className="fixed inset-0 bg-black opacity-60 transition duration-300 data-[closed]:opacity-0" />
			<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
				<div className="flex min-h-full items-start justify-center">
					<DialogPanel
						transition
						style={{ width }}
						className={`rounded-xl bg-white p-4 shadow-lg m-4 max-h-[calc(100vh_-_4rem)]
						transition ease-out duration-300
						data-[closed]:opacity-0 data-[closed]:-translate-y-full`}
					>
						{/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
						<Component isOpen={isOpen} data={row!} close={closeMe} />
					</DialogPanel>
				</div>
			</div>
		</Dialog>
	})
}
